// src/providers/QueryProvider.tsx
import { ReactNode, useMemo } from 'react';

import {
  DefaultOptions,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

interface QueryProviderOptions {
  queryCache?: QueryCache;
  mutationCache?: MutationCache;
  defaultOptions?: DefaultOptions;
}

interface QueryProviderProps {
  children: ReactNode;
  devtools?: boolean;
  options?: QueryProviderOptions;
}

export const QueryProvider = ({ children, options }: QueryProviderProps) => {
  const { queryCache, mutationCache, defaultOptions } = options ?? {};
  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: 0,
          ...defaultOptions?.queries,
        },
        mutations: {
          retry: 0,
          ...defaultOptions?.mutations,
        },
      },
      queryCache,
      mutationCache,
    });
  }, [options]);

  const isDevMode = import.meta.env.DEV;
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {isDevMode && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom" />
      )}
    </QueryClientProvider>
  );
};
